import React, {useEffect, useReducer} from "react"
import {Checkbox, Col, Row} from "antd"
import {isEqual} from "lodash"
import {useTranslation} from "react-i18next"
import {BaseButton, SecondaryButton} from "components/buttons"
import {generalSettingsService} from "services"
import {handleError, toastError} from "helpers"
import {PermissionsRequired} from "components/PermissionsRequired"
import {Permissions} from "types/permission"
import styles from "./PracticalSettings.module.css"
import {FormLabel} from "components/Form"
import {GeneralSettingField} from "types/settings/general-setting"
import {useMutation, useQuery} from "@tanstack/react-query"
import {YesNo} from "types/common"

function reducer(state, action) {
    return {...state, ...action}
}

export function PracticalSettings() {
    const initialState = {
        isSubmitting: false,
        isPracticalSyncStatusEnabled: false,
        oldIsPracticalSyncStatusEnabled: false
    }

    const [{isSubmitting, isPracticalSyncStatusEnabled, oldIsPracticalSyncStatusEnabled}, dispatch] = useReducer(
        reducer,
        initialState
    )
    const {t} = useTranslation(["academics", "common"])

    const updateGeneralSettings = useMutation(
        async (payload: Array<{field: GeneralSettingField; checked?: boolean; value?: string}>) => {
            await generalSettingsService.updateSettings(
                payload.map(({field, checked, value}) => ({
                    field,
                    value: checked ? YesNo.Yes : YesNo.No,
                    ...(value && {value})
                }))
            )
        },
        {
            onError: (error: Error) => {
                toastError(error.message)
            }
        }
    )

    const generalSettingsQuery = useQuery(
        ["general-setting"],
        async () => {
            return await generalSettingsService.getSettings([GeneralSettingField.IsPracticalSyncStatusEnabled])
        },
        {
            initialData: []
        }
    )

    useEffect(() => {
        if (!generalSettingsQuery.isFetched) return
        const isPracticalSyncStatusEnabled = generalSettingsQuery.data?.find(
            (item) => item.field === GeneralSettingField.IsPracticalSyncStatusEnabled
        )
        dispatch({
            isPracticalSyncStatusEnabled: isPracticalSyncStatusEnabled?.value === YesNo.Yes,
            oldIsPracticalSyncStatusEnabled: isPracticalSyncStatusEnabled?.value === YesNo.Yes
        })
    }, [generalSettingsQuery.isFetched, generalSettingsQuery.data])

    function onClickCancel() {
        dispatch({isPracticalSyncStatusEnabled: oldIsPracticalSyncStatusEnabled})
    }

    async function onClickSave() {
        try {
            dispatch({isSubmitting: true})
            await updateGeneralSettings.mutateAsync([
                {field: GeneralSettingField.IsPracticalSyncStatusEnabled, checked: isPracticalSyncStatusEnabled}
            ])
            await generalSettingsQuery.refetch()
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isSubmitting: false})
        }
    }

    const isChanged = !isEqual(isPracticalSyncStatusEnabled, oldIsPracticalSyncStatusEnabled)

    return (
        <div className={styles.wrapper}>
            <div className={styles.settingWrap}>
                <Row gutter={[24, 24]} style={{marginTop: 16}}>
                    <Col span={12}>
                        <div className={styles.fieldWrap}>
                            <FormLabel label="Enable Synced Status" />
                            <Checkbox
                                checked={isPracticalSyncStatusEnabled}
                                onChange={(event) => dispatch({isPracticalSyncStatusEnabled: event.target.checked})}>
                                Enabled
                            </Checkbox>
                        </div>
                    </Col>
                </Row>

                <PermissionsRequired permissions={{staff: [Permissions.Staff.Settings.Modules.Academics.Edit]}}>
                    {isChanged && (
                        <div className={styles.buttonWrap}>
                            <SecondaryButton
                                title={t("common:action.cancel")}
                                onClick={onClickCancel}
                                className={styles.cancelBtn}
                            />
                            <BaseButton
                                title={t("common:action.save").toUpperCase()}
                                onClick={onClickSave}
                                loading={isSubmitting}
                            />
                        </div>
                    )}
                </PermissionsRequired>
            </div>
        </div>
    )
}
